// DLC 11 — otwieranie rysunku w przeglądarce.
//
// Rysunek DXF/DWG da się obejrzeć bez CAD-a na komputerze: silnik jest skompilowany
// do WebAssembly i stoi pod /static/app/ na naszym serwerze (patrz viewer/README.md).
//
// Viewer to OSOBNY dokument i nie ma naszego tokena — dlatego najpierw prosimy backend
// o krótkotrwały bilet na ten jeden rysunek, a dopiero potem otwieramy kartę z adresem,
// który ten bilet zawiera.
import apiClient from "@/api/client";

// Wskazujemy plik wprost, a nie sam katalog: ani `runserver` w dev, ani WhiteNoise
// na produkcji nie serwują domyślnie zawartości katalogu i adres kończyłby się 404.
const ADRES_VIEWERA = "/static/app/index.html";

interface BiletNaRysunek {
  url: string;
  filename: string;
}

/** Rysunek przedsięwzięcia, który TEN użytkownik może otworzyć. */
export interface Rysunek {
  id: string;
  nazwa: string;
  /** Skąd pochodzi: „Trasa — wariant B — wersja 3", „Podkład", albo folder z zakładki Pliki. */
  skad: string;
  rozmiar: number | null;
  /** Obowiązujący przebieg — ten, którym żyje całe przedsięwzięcie. Zawsze najwyżej jeden. */
  glowny: boolean;
}

export const pobierzRysunki = async (connectionId: string): Promise<Rysunek[]> => {
  const { data } = await apiClient.get<{ data: Rysunek[] }>(
    `/cad/connections/${connectionId}/rysunki/`,
  );
  return data.data;
};

/** Adres, pod który można wskazać ramkę albo kartę. Bilet ważny 5 minut. */
export async function adresRysunku(managedFileId: string): Promise<string> {
  const { data } = await apiClient.post<{ data: BiletNaRysunek }>(
    `/cad/rysunki/${managedFileId}/bilet/`,
  );
  return adresViewera(data.data);
}

// Rozpoznajemy po rozszerzeniu — tak samo jak backend (`ROZSZERZENIA` w services.py).
export const jestRysunkiem = (nazwa: string): boolean =>
  /\.(dxf|dwg)$/i.test(nazwa.trim());

export const adresViewera = (bilet: BiletNaRysunek): string =>
  `${ADRES_VIEWERA}?file=${encodeURIComponent(bilet.url)}` +
  `&name=${encodeURIComponent(bilet.filename)}`;

/**
 * Otwiera rysunek w nowej karcie.
 *
 * Kartę otwieramy OD RAZU po kliknięciu, jeszcze pustą, a adres wstawiamy dopiero
 * gdy przyjdzie bilet. Odwrotna kolejność nie działa: przeglądarka blokuje nowe karty
 * otwierane po zakończeniu obsługi kliknięcia i użytkownik nie widzi nic.
 *
 * Gdy backend odmówi (np. rysunek stał się poufny), pustą kartę zamykamy i błąd wraca
 * do wywołującego, żeby pokazać go tam, gdzie użytkownik patrzy.
 */
export async function otworzRysunek(managedFileId: string): Promise<void> {
  // BEZ "noopener": z tą opcją `window.open` zwraca null i nie ma czego przekierować —
  // użytkownik dostaje pustą kartę. Zamiast tego odcinamy powiązanie po ustawieniu
  // adresu, co daje ten sam efekt, a uchwyt zostaje.
  const karta = window.open("", "_blank");
  try {
    const adres = await adresRysunku(managedFileId);
    if (karta) {
      karta.location.href = adres;
      karta.opener = null;
    }
  } catch (e) {
    karta?.close();
    throw e;
  }
}
